import { formatMoney, formatDecimal, tt } from '@agos/shared'

/**
 * Where an order's discount came from — FRONTEND.md §9.210.
 *
 * The archive drew `Zľava −4,20 €` and never said it was `OBED2026`. `couponCode` has been on the
 * payload all along — 745 orders carry one — and no manager screen had ever read it.
 *
 * **Four answers, and the fourth is the interesting one.** Measured on the restored data: 745 orders
 * carry a coupon, 233 a manual reduction with the employee's name on it, none redeemed points against
 * a discount — and **875 of the 1 853 discounted orders have none of the three**. Nearly half the
 * discounts in the owner's history have no recorded reason, and the screen says so rather than
 * falling through to a bare *Zľava* that reads as if there were nothing more to add.
 */

export interface DiscountedOrder {
  discount?: number | string | null
  couponCode?: string | null
  manualDiscountType?: string | null
  manualDiscountValue?: number | string | null
  manualDiscountOperator?: string | null
  redeemedPoints?: number | null
}

export type DiscountKind = 'coupon' | 'manual' | 'points' | 'unrecorded' | 'none'

export interface DiscountSource {
  kind: DiscountKind
  /** What the badge and the bill line say. */
  label: string
  /** Who applied it, when that is a person rather than a rule. */
  operator: string | null
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

function amount(value: unknown): number {
  const parsed = typeof value === 'number' ? value : Number(text(value))
  return Number.isFinite(parsed) ? parsed : 0
}

/**
 * `10` means ten per cent and `5.00` means five euros — one column, two meanings, and
 * `manualDiscountType` is what tells them apart.
 *
 * Printed as stored rather than derived from the money: a line added after the reduction was typed
 * would make a recomputed percentage disagree with what the operator actually chose.
 */
export function manualDiscountLabel(order: DiscountedOrder): string {
  const value = amount(order.manualDiscountValue)
  if (order.manualDiscountType === 'percent') {
    return `${tt('Ručná zľava')} ${formatDecimal(value, value % 1 === 0 ? 0 : 1)} %`
  }
  return `${tt('Ručná zľava')} ${formatMoney(value)}`
}

export function discountSource(order: DiscountedOrder): DiscountSource {
  if (amount(order.discount) <= 0) {
    return { kind: 'none', label: '', operator: null }
  }

  const coupon = text(order.couponCode)
  if (coupon !== '') {
    return { kind: 'coupon', label: `${tt('Kupón')} ${coupon}`, operator: null }
  }

  if (text(order.manualDiscountType) !== '') {
    return {
      kind: 'manual',
      label: manualDiscountLabel(order),
      // The point of storing the operator: a reduction typed at a till is a decision with a name on
      // it, and the archive is where it is looked up.
      operator: text(order.manualDiscountOperator) || null,
    }
  }

  if ((order.redeemedPoints ?? 0) > 0) {
    return { kind: 'points', label: tt('Vernostné body'), operator: null }
  }

  // 875 of 1 853. Saying nothing here would read as "the screen has nothing to add"; it has.
  return { kind: 'unrecorded', label: tt('Zdroj zľavy nie je zaznamenaný'), operator: null }
}
